import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";

import PropTypes from "prop-types";

import { MaskedTextBox } from "@progress/kendo-react-inputs";
import { Calendar, TimeSelector } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import { useInternationalization } from "@progress/kendo-react-intl";

import { useValidation } from "../utils/ValidationProvider";
import { doubleChar, pickFromString, replaceRange } from "../utils/helper";
import { dateToFormattedString, extractFormat, formatRemoveFancyName, getFormat, getFormatString } from "../utils/convert";

import "./DateInput.scss";


const getMask = (props) => {
  // handle MMM-MMMM, ddd-dddd
  return (getFormat(props.format, props.type, props.disabled)
    .replace(/[fhHmsy]/g, "0")
    .replace("dddd", "LLLL")
    .replace("ddd", "LLL")
    .replace("dd", "00")
    .replace("d", "0")
    .replace("MMMM", "LLLL")
    .replace("MMM", "LLL")
    .replace("MM", "00")
    .replace("M", "0")
    .replace(/[t]/g, "L")
    .replace(/[z]/g, "A")
  );
};

const getStateFromProps = (props, intl) => {
  return {
    // value: props.value instanceof Date ? dateToString(props.value, props.type !== "time", props.type !== "date") : "",
    value: props.value instanceof Date ? dateToFormattedString(props.value, props.type, getFormat(props.format, props.type, props.disabled), intl) : "",
    date: props.value instanceof Date ? props.value : null,
    valid: false,
    mask: getMask(props),
    format: getFormat(props.format, props.type, props.disabled),
    useTime: props.type === "datetime" || props.type === "time" || /[fHms]/g.test(getFormat(props.format, props.type, props.disabled)),
    useDate: props.type !== "time" || /[dMy]/g.test(getFormat(props.format, props.type, props.disabled)),
    steps: {
      hour: props.steps?.hour,
      minute: props.steps?.minute,
    },
    tab: 0,
    valueInvalid: false,
  };
};

/**
 * Date picker using hooks
 * @module
 * @param {string} name - Name
 * @param {string} type - date or datetime or time
 * @param {string} [format] - specify custom or kendo format, if null or undefined, default format for type is used
 * @param {func} onChange - Function invoked on value change
 * @param {object} steps - object containing required hour/minute increment
 * @param {Date} [value] - Current input value
 * @param {bool} [disabled] - Is input readonly? => user can not change value
 * @param {bool} [required] - Is input required? => can not be left empty
 * @param {bool} [validationHidden] - Is validation hidden
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {Date} [min] - Minimum date
 * @param {Date} [max] - Maximum date
 * @param {number} [tabIndex] - Tabindex of datepicker => index of input using <tab> to navigate
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {bool} [noFormInput] - Indicates if the input should not have form-input class
 *
 *              Date                         Format : format je fixny v tvare "dd.mm.yyyy" !!!!
 *              DateTime                     Format : format je fixny v tvare "dd.mm.yyyy HH:MM" !!!!
 */

export const DateInput = forwardRef((props, ref) => {
  const intl = useInternationalization();

  const [state, setState] = useState(getStateFromProps(props, intl));
  const [calendarOpen, setCalendarOpen] = useState(false);

  const anchor = useRef(null);
  const popupRef = useRef(null);
  const popupInsideRef = useRef(null);
  const inputRef = useRef(null);

  // for useEffect event handling
  let eventHolder = useRef(null);
  let yearAutocompleteWait = useRef(false);

  useImperativeHandle(ref, () => {
    return {
      stringToDate,
      dateToString: dateToStringInternal,
      dateInRange,
      normalise,
      getToday,
      onChange,
      state: {
        value: state.value,
        date: state.date
      }
    };
  });

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate: validateInput, setValidationValue } = useValidation(field, props.value);

  useEffect(() => {
    // set state from props
    if (props.value !== state.date) {
      setState((prev) => ({
        ...prev,
        value: props.value instanceof Date ? dateToFormattedString(props.value, props.type, getFormat(props.format, props.type, props.disabled), intl) : "",
        date: props.value instanceof Date ? props.value : null,
      }));
      if (props.value instanceof Date) {
        setValidationValue(props.value);
      }
    }
  }, [props.value, state.date]);

  useEffect(() => {
    setState(getStateFromProps(props, intl));
    // eslint-disable-next-line
  }, [props.value, props.format]);

  useEffect(() => {
    setCursorToDefaultPos();

    if (!eventHolder.current) return;

    if (eventHolder.current?.syntheticEvent?.type === "click" && state.value === "") onClear();
    if (eventHolder.current?.syntheticEvent?.type === "change" && state.mask.replace(/[0LA]/g, "_") === state.value && eventHolder.current?.nativeEvent?.data === null) onClear();

    // eslint-disable-next-line
  }, [state.value, state.date, state.valueInvalid]);

  useEffect(() => {
    if (calendarOpen)
      popupInsideRef.current.focus();
  }, [calendarOpen]);

  const setCursorToDefaultPos = () => {
    if (
      inputRef.current
      && (!state.value || state.mask.replace(/[0LA]/g, "_") === state.value)
      && (inputRef.current._input.selectionStart || inputRef.current._input.selectionEnd)
    ) {
      setTimeout(() => {
        if (!inputRef.current) return;
        inputRef.current._input.selectionStart = 0;
        inputRef.current._input.selectionEnd = 0;
        inputRef.current._selection = [0, 0];
      }, 0);
    }
  };

  const stringToDate = (val, outformat) => {
    if (!val || !val.replace(/[aA-zZ\-.,_ :/]/g, "")) return null;
    if (!validate(val)) return null;

    return intl.parseDate(val, outformat ?? state.format);
  };

  const dateToStringInternal = (date) => {
    return dateToFormattedString(date, props.type, state.format, intl);
  };

  const dateInRange = (date, min, max) => {
    if (!state.useDate) return true;
    if (date === undefined || date === null) return true;
    if (min === undefined || min === null) min = date;
    if (max === undefined || max === null) max = date;

    // careful of time
    if (!state.useTime) {
      min.setHours(0, 0, 0, 0);
      max.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
    }

    return min <= date && date <= max;
  };

  const normalise = (value) => {
    if ([null, undefined].includes(value)) return value;
    return value.toString().length > 1 ? value.toString() : `0${value}`;
  };

  const normaliseTo = (value, to) => {
    if ([null, undefined].includes(value)) return value;
    let res = "";

    for (let i = 0; i < to - value.toString().length; i++) {
      res = res + "0";
    }
    return `${res}${value}`;
  };

  const correctDate = (value) => {
    let newValue = "";
    let day, month, year, hour, minute, second = "";
    const formatString = getFormatString(state.format);

    if (state.useDate) {

      // check year
      const [yearStart, yearCount] = pickFromString(formatString, /[yY]/);
      if (yearStart >= 0) {
        if (!(new RegExp(`^.{${yearStart}}([0-9]{${yearCount}})`)).test(value)) return null;
        year = value.substring(yearStart, yearStart + yearCount);
        newValue = replaceRange(value, normaliseTo(year, yearCount), yearStart, yearStart + yearCount);
      }

      // check month
      const [monthStart, monthCount] = pickFromString(formatString, /[M]/, 2);
      if (monthStart >= 0) {
        if (!(new RegExp(`^.{${monthStart}}([0-9]{${monthCount}})`)).test(value)) return null;
        month = parseInt(value.substring(monthStart, monthStart + monthCount));
        if (month <= 0) month = 1;
        if (month > 12) month = 12;

        month = normalise(month);
        newValue = replaceRange(newValue === "" ? value : newValue, month, monthStart, monthStart + monthCount);
      }

      // check day
      const [dayStart, dayCount] = pickFromString(formatString, /[d]/, 2);
      let d = new Date(year, month, 0);

      if (dayStart >= 0) {
        if (!(new RegExp(`^.{${dayStart}}([0-9]{${dayCount}})`)).test(value)) return null;

        day = parseInt(value.substring(dayStart, dayStart + dayCount));
        if (day <= 0) day = 1;
        if (day > d.getDate()) day = d.getDate();

        day = normalise(day);
        newValue = replaceRange(newValue === "" ? value : newValue, day, dayStart, dayStart + dayCount);
      }

      if (!state.useTime) return newValue;
    }

    // time
    // check hour

    const [hourStart, hourCount, hourMask] = pickFromString(formatString, /[hH]/, 2);
    const [minuteStart, minuteCount] = pickFromString(formatString, /[m]/, 2);
    const [secondStart, secondCount] = pickFromString(formatString, /[s]/, 2);

    if (hourStart >= 0) {
      if (!new RegExp(`^.{${hourStart}}([0-9]{${hourCount}})`).test(value)) return null;
      hour = parseInt(value.substring(hourStart, hourStart + hourCount));
      let maxHour = hourMask.includes("H") ? 24 : 13;

      if (hour < 0) hour = 0;
      if (hour >= maxHour) hour = maxHour - 1;

      const newHour = normalise(hour);
      newValue = replaceRange(newValue === "" ? value : newValue, newHour, hourStart, hourStart + hourCount);
    }

    // minute
    if (minuteStart >= 0) {
      if (!new RegExp(`^.{${minuteStart}}([0-9]{${minuteCount}})`).test(value)) return null;
      minute = parseInt(value.substring(minuteStart, minuteStart + minuteCount));
      if (minute < 0) minute = 0;
      if (minute > 59) minute = 59;

      const newMinute = normalise(minute);
      newValue = replaceRange(newValue === "" ? value : newValue, newMinute, minuteStart, minuteStart + minuteCount);
    }

    // second
    if (secondStart >= 0) {
      if (!new RegExp(`^.{${secondStart}}([0-9]{${secondCount}})`).test(value)) return null;
      second = parseInt(value.substring(secondStart, secondStart + secondCount));
      if (second < 0) second = 0;
      if (second > 59) second = 59;

      second = normalise(second);
      newValue = replaceRange(newValue === "" ? value : newValue, second, secondStart, secondStart + secondCount);
    }

    return newValue;
  };

  const getToday = () => {
    return dateToStringInternal(new Date());
  };

  const validate = (value) => {
    if (!value) return false;
    if (!value.replace(/[aA-zZ\-.,_ :/]/g, "") || value.includes("_")) return false;
    if (intl.parseDate(value, state.format) !== null) return true;
    return false;
  };

  const validateOnlyDate = (value, includeYear = true) => {
    if (!value) return false;
    const formatString = getFormatString(state.format);

    const [yearStart, yearCount] = pickFromString(formatString, /[yY]/);
    const validYear = new RegExp(`^.{${yearStart}}([0-9]){${yearCount}}`);

    const [dayStart] = pickFromString(formatString, /[d]/, 2);
    const validDay = new RegExp(`^.{${dayStart}}([1-2][0-9])|^.{${dayStart}}(0[1-9])|^.{${dayStart}}30|^.{${dayStart}}31`);

    const [monthStart] = pickFromString(formatString, /[M]/, 2);
    const validMonth = new RegExp(`^.{${monthStart}}(0[1-9])|^.{${monthStart}}(1[0-2])`);

    return (
      (includeYear ? validYear.test(value) : true) &&
      (dayStart >= 0 ? validDay.test(value) : true) &&
      (monthStart >= 0 ? validMonth.test(value) : true)
    );
  };

  const autoComplYear = (value, nativeEvent) => {
    if (!state.useDate || !value) return [value];

    const formatString = getFormatString(state.format);
    const [yearStart, yearCount] = pickFromString(formatString, /[yY]/);

    if (value.substring(yearStart, yearStart + yearCount).replace(/[_]/g, "").trim() !== "") return [value];
    if (!validateOnlyDate(value, false) || validate(value)) return [value];

    const newYearValue = new Date().getFullYear();
    let newValue = replaceRange(value, `${newYearValue}`.slice(-yearCount), yearStart, yearStart + yearCount);

    return [newValue, (newValue !== value) ? yearStart : null];
  };

  const autoComplTime = (value, nativeEvent) => {

    const formatString = getFormatString(state.format);
    // get time form value
    let newVal = "";

    const [hourStart, hourCount] = pickFromString(formatString, /[hH]/, 2);
    const [minuteStart, minuteCount] = pickFromString(formatString, /[m]/, 2);
    const [secondStart, secondCount] = pickFromString(formatString, /[s]/, 2);

    let start = Math.min(hourStart >= 0 ? hourStart : 9999, minuteStart >= 0 ? minuteStart : 9999, secondStart >= 0 ? secondStart : 9999);
    let end = Math.max(hourStart + hourCount, minuteStart + minuteCount, secondStart + secondCount);

    // order by start ascending and filter out not used items
    const orderedArr = [[hourStart, hourCount], [minuteStart, minuteCount], [secondStart, secondCount]].sort((a, b) => a[0] - b[0]).filter(item => item[0] >= 0);

    if (start < 0 || end < start) return [value];
    newVal = value.substring(start, end).trim();
    if (!newVal) return [value];

    // move and replace 01:50 to 15:02
    if (validate(state.value)) {
      // if (validate(value)) {

      if (!nativeEvent) return [value];
      if (!/[0-9]/g.test(nativeEvent.data)) return [value];

      if (newVal[0] !== "0") return [value];

      newVal = newVal.replace(":", "").replace(/[ _]/g, "");

      if (
        newVal.length < 4 ||
        // make sure you are adding at the end, meaning selection is end + 1
        inputRef.current._input.selectionStart <= end
      )
        return [value];

      newVal = newVal.substring(1) + nativeEvent.data;
      // fill newVal to value: 1235 => date ... 12:35 ... 
      let _ = value;
      for (let i = 0; i < orderedArr.length; i++) {
        _ = replaceRange(_, newVal.substring(i * 2, i * 2 + 2), orderedArr[i][0], orderedArr[i][0] + orderedArr[i][1]);
      }
      newVal = _;

      if (!validate(newVal)) return [value];

      return [newVal, end];
    }

    // classic autocomplete
    // cut from end and start only and not from middle
    newVal = newVal.replace(":", "").replace(/^_+|_+$/g, "");
    if (newVal.length !== 3) return [value];
    newVal = "0" + newVal;

    let _ = value;
    for (let i = 0; i < orderedArr.length; i++) {
      _ = replaceRange(_, newVal.substring(i * 2, i * 2 + 2), orderedArr[i][0], orderedArr[i][0] + orderedArr[i][1]);
    }
    newVal = _;

    if (!validate(newVal)) return [value];

    return [newVal, end];
  };

  const roundToClosest = (num, closest = 1) => {
    if (!num) return;
    if (num % closest === 0) return num;

    let newNum = num;
    if ((newNum % closest) >= Math.round(closest / 2)) {
      newNum += closest - (newNum % closest);
    } else {
      newNum -= newNum % closest;
    }

    return newNum;
  };

  const roundTime = (value) => {
    if (!value || !validate(value)) return value;

    let overflowFormMinute = 0;

    const formatString = getFormatString(state.format);

    // round Minute
    let minute = "";
    const [minuteStart, minuteCount] = pickFromString(formatString, /[m]/, 2);
    let rangeMinute = { start: minuteStart, end: minuteStart + minuteCount };
    minute = value.substring(rangeMinute.start, rangeMinute.end);

    if (state.steps.minute) {
      let rMinute = roundToClosest(parseInt(minute), state.steps.minute);

      if (rMinute > 59) overflowFormMinute = 1;
      minute = rMinute > 59 ? 0 : rMinute;
    }

    // round Hour 
    let hour = "";
    const [hourStart, hourCount, hourMask] = pickFromString(formatString, /[hH]/, 2);
    let rangeHour = { start: hourStart, end: hourStart + hourCount };
    hour = parseInt(value.substring(rangeHour.start, rangeHour.end)) + overflowFormMinute;

    if (state.steps.hour) {
      let rHour = roundToClosest(hour, state.steps.hour);
      hour = rHour;
    }
    let maxHour = hourMask.includes("H") ? 23 : 12;
    hour = hour > maxHour ? 0 : hour;

    // connect
    hour = normalise(hour);
    minute = normalise(minute);
    let newValue = replaceRange(value, hour, hourStart, hourStart + hourCount);
    newValue = replaceRange(newValue, minute, minuteStart, minuteStart + minuteCount);

    return newValue;
  };

  const autoCmpWait = (newValue, oldValue, pos) => {
    clearTimeout(yearAutocompleteWait.current);

    yearAutocompleteWait.current = setTimeout(() => {
      if (!inputRef.current || oldValue !== inputRef.current.props.value) return;

      let correctedValue = correctDate(newValue);
      if (correctedValue !== null) newValue = correctedValue;

      const valid = validate(newValue);
      let date = null;
      if (valid) date = stringToDate(newValue);

      if (props.validationAllowed) {
        if (date) {
          setValidationValue(date);
        } else {
          setValidationValue(newValue);
        }
      }

      if (props.onChange && date) props.onChange(props.name, date);
      setState((prev) => ({
        ...prev,
        value: newValue,
        valid: valid,
        date: date,
        valueInvalid: !(
          valid && dateInRange(date, props.min, props.max)
        ),
      }));

      inputRef.current._selection = [
        pos ?? inputRef.current._selection[0],
        pos ?? inputRef.current._selection[1],
      ];
    }, 350);
  };

  const dotCmp = (e, value) => {
    if (!e || e.nativeEvent?.data !== "." || e.selectionStart !== e.selectionEnd) return [value, null];
    const dotReg = /[1-9]_[.]/;

    const sub = value.substring(e.selectionStart - 1, e.selectionStart + 2);
    // try match eg. "3_." at selection
    if (!dotReg.test(sub)) return [value, null];

    let newDot = `0${sub[0]}.`;
    value = replaceRange(value, newDot, e.selectionStart - 1, e.selectionStart + 2);

    return [value, e.selectionStart + 2];
  };

  const colonCmp = (e, value) => {
    if (!e || e.nativeEvent?.data !== ":" || e.selectionStart !== e.selectionEnd) return [value, null];
    const dotReg = /[0-9]_[:]/;

    const sub = value.substring(e.selectionStart - 1, e.selectionStart + 2);
    // try match eg. "3_:" at selection
    if (!dotReg.test(sub)) return [value, null];

    let newDot = `0${sub[0]}:`;
    value = replaceRange(value, newDot, e.selectionStart - 1, e.selectionStart + 2);

    return [value, e.selectionStart + 2];
  };

  const onChange = async (e) => {
    eventHolder.current = e;
    let newValue = e?.value;

    let dotPos = null;
    [newValue, dotPos] = dotCmp(e, newValue);

    let colonPos = null;
    [newValue, colonPos] = colonCmp(e, newValue);

    let fpos = null;
    if (newValue === state.mask.replace(/[0LA]/g, "_") && /[0-9]/g.test(e?.nativeEvent.data) && e.selectionStart === e.selectionEnd && e.selectionStart === newValue.length) {
      newValue = e?.nativeEvent.data + newValue.substring(1);
      fpos = 1;
    }

    let correctedValue = correctDate(newValue);
    if (correctedValue !== null) newValue = correctedValue;

    let pos = state.mask.replace(/[0LA]/g, "_") === newValue ? 0 : null;
    let newPos = null;
    let newPosFromYear = null;

    let autocmpValue = null;
    let autocmpPos = null;
    if (state.useTime) {
      [autocmpValue, autocmpPos] = autoComplTime(newValue, e?.nativeEvent);
    }

    [autocmpValue, newPosFromYear] = autoComplYear(autocmpValue ?? newValue, e?.nativeEvent);
    // warn: has to be after all autocompletes / value edits
    if (autocmpValue !== newValue && autocmpValue) autoCmpWait(autocmpValue, newValue, newPosFromYear ?? autocmpPos);
    newPosFromYear = null;

    pos = dotPos ?? colonPos ?? fpos ?? newPos ?? newPosFromYear ?? pos;

    // nemoze byt tu lebo si navzajom s autocomplete prepisuju hodnoty
    // if (validate(value) && state.useTime) value = roundTime(value);

    const valid = validate(newValue);

    let date = null;
    if (valid) date = stringToDate(newValue);
    if (valid && !dateInRange(date, props.min, props.max)) {
      date = date < props.min ? props.min : props.max;
      newValue = dateToFormattedString(date, state.type, state.format, intl);
    }

    if (props.validationAllowed) {
      if (date) {
        setValidationValue(date);
      } else {
        setValidationValue(newValue);
      }
    }

    if (props.onChange && date) await props.onChange(props.name, date);
    setState((prev) => ({
      ...prev,
      value: newValue,
      date: date, // netreba nastavi sa v useEffecte
      valid: valid,
      valueInvalid: !(
        valid && dateInRange(date, props.min, props.max)
      ),
    }));

    inputRef.current._selection = [
      pos ?? inputRef.current._selection[0],
      pos ?? inputRef.current._selection[1],
    ];
  };

  const onClear = () => {
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];
    if (props.onClear) props.onClear(state.value);
    if (props.onChange) props.onChange(props.name, null, eventHolder.current);
  };

  const onMaskedTBFocus = () => {
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];

    if (!state.value) {
      setState((prev) => ({
        ...prev,
        value: prev.mask.replace(/[0LA]/g, "_")
      }));
    }
  };

  // const selectTab = (val) => {
  //   setState({
  //     ...state,
  //     tab: val.selected,
  //   });
  // };

  const onBlur = (e) => {
    validateInput();
    // leftover selection causes issues in grid edit
    inputRef.current._selection = [null, null];

    /*if (!state.calendarOpen) {
      onChange({
        value: state.value,
      });
    }*/

    // tu treba zavolat onchange lebo sa opravuje datum podla step, inak neopravuje detailData
    let value;
    if (validate(state.value) && state.useTime) value = roundTime(state.value);
    if (value && (state.steps.hour !== undefined || state.steps.minute !== undefined)) onChange({ value });

    // vyprazdenie masky
    if (state.value === state.mask.replace(/[0LA]/g, "_")) {
      setState((prev) => ({
        ...prev,
        value: ""
      }));
    }

    clearTimeout(yearAutocompleteWait.current);
    if (props.onBlur) props.onBlur(e);
  };

  const cancelClosePopup = () => {
    clearTimeout(popupRef.current);
  };

  const closePopup = () => {
    clearTimeout(popupRef.current);
    popupRef.current = setTimeout(() => {
      setCalendarOpen(false);
    }, 200);
  };

  const onChangePopup = (e, close = true) => {
    if (!e) return;
    if (!e.value) return;

    onChange({ ...e, value: dateToStringInternal(e.value) });
    if (close) {
      setCalendarOpen(false);
    }
    validateInput();
    // closePopup();
  };

  const buildPopUpContent = () => {
    if (state.useDate && !state.useTime) {
      return (
        <Calendar
          onChange={onChangePopup}
          min={props.min ?? undefined}
          max={props.max ?? undefined}
          value={stringToDate(state.value ?? "")}
        />
      );
    } else if (!state.useDate && state.useTime) {
      return (
        <TimeSelector
          onChange={onChangePopup}
          value={state.date}
          cancelButton={false}
          steps={state.steps}
        />
      );
    } else {
      return (
        <div className={"dateTimeSelector"} style={{ display: 'flex', flexDirection: 'row' }}>
          <Calendar
            onChange={(e) => onChangePopup(e, false)}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            value={stringToDate(state.value ?? "")}
          />
          <TimeSelector
            onChange={onChangePopup}
            value={state.date}
            cancelButton={false}
            steps={state.steps}
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className="k-dateinput-wrap wrap">
        <span
          className={`${props.noFormInput ?? "form-input"} dpop k-input k-input-md k-input-solid k-datepicker datepicker ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${state.valueInvalid ? "dpop-invalid" : ""}`}
          onFocus={props.onFocus}
          ref={anchor}
          onBlur={onBlur}
        >
          <MaskedTextBox
            ref={inputRef}
            className={"k-dateinput k-input masked-tb " + (props.className ?? "")}
            includeLiterals={true}
            name={props.name}
            valid={!state.valueInvalid || props.disabled}
            mask={state.mask}
            placeholder={props.placeholder}
            value={state.value}
            onChange={onChange}
            onFocus={onMaskedTBFocus}
            disabled={props.disabled}
            validityStyles={null}
            required={props.required}
            tabIndex={props.tabIndex}
          >
          </MaskedTextBox>
          {!props.disabled &&
            <div className={`k-icon k-font-icon k-i-close datepicker-reset${(props.disabled || !state.value) ? '-disabled' : ''} ${!props.todayHidden ? 'today' : ''} ${props.nobuttons ? 'dateinput-nobuttons' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                inputRef.current._selection = [0, 0];
                onChange({ syntheticEvent: e, nativeEvent: e.nativeEvent, target: e.target, value: "" });
                anchor?.current?.focus();
              }}
            ></div>
          }

          {props.nobuttons || props.disabled ? (
            <></>
          ) : (
            <>
              <div
                className={`but k-icon k-font-icon ${state.useDate ? `k-i-calendar${props.disabled ? "-disabled" : ""}` : `k-i-clock${props.disabled ? "-disabled" : ""}`}  datepicker-open`}
                onClick={(e) => {
                  e.preventDefault();
                  setCalendarOpen(!calendarOpen);
                }}
              />
              {!props.todayHidden && state.useDate && (
                <div
                  className={`but but-primary k-icon k-font-icon k-i-calendar-date${props.disabled ? "-disabled" : ""} datepicker-today`}
                  onClick={(e) => {
                    e.preventDefault();
                    let today = getToday();

                    // auto round
                    if (validate(today) && state.useTime) today = roundTime(today);
                    onChange({ ...e, value: today });
                    props.validationAllowed && setValidationValue(stringToDate(today));
                    validateInput();
                  }}
                />
              )}
            </>
          )}

          <Popup
            anchor={anchor.current}
            show={calendarOpen}
            ref={popupRef}
            className={"date-input-popup-wrapper"}
          >
            <div
              tabIndex="0"
              onBlur={closePopup}
              onFocus={cancelClosePopup}
              ref={popupInsideRef}
              className="date-input-popup"
            >
              {buildPopUpContent()}
            </div>
          </Popup>
        </span>
        {!(!state.valueInvalid || props.disabled) && !props.validationHidden && (
          <div className="value-warn">
            {props.validationMessage || "Zadaná hodnota nemá požadovaný formát"}
          </div>
        )}
      </div>

    </>
  );
});

DateInput.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  steps: PropTypes.object,
  value: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  todayHidden: PropTypes.bool,
  validationHidden: PropTypes.bool,
  validationMessage: PropTypes.string,
  onBlur: PropTypes.func,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  tabIndex: PropTypes.number,
  noFormInput: PropTypes.bool
};

export default DateInput;
